var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Autocomplete from '@mui/material/Autocomplete';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Input } from '../input';
import MultiSelectSelectedTags from './multi-select-selected-tags';
import { debounce } from 'lodash';
import { lighten, darken, styled } from '@mui/material';
const DEFAULT_LIMIT = 10;
const DEFAULT_PAGE = 0;
const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'light'
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
}));
const GroupItems = styled('ul')({
    padding: 0,
});
export const ServerSideMultiSelect = (_a) => {
    var { defaultPage = DEFAULT_PAGE, defaultLimit = DEFAULT_LIMIT, lazyQuery, multiple, allowSelectAll, useCheckboxOption, value: valueProp, defaultValue, onChange: onChangeProp, shouldGroup = false, loading: loadingProp = false, renderInput, inputProps, error, parseData, getOptionLabel = (option) => {
        var _a;
        if (typeof option === 'string') {
            return option;
        }
        if (Array.isArray(option)) {
            return ((_a = option === null || option === void 0 ? void 0 : option.at(0)) === null || _a === void 0 ? void 0 : _a.name) || '';
        }
        return (option === null || option === void 0 ? void 0 : option.name) || '';
    }, getOptionKey = (option) => {
        var _a;
        if (typeof option === 'string') {
            return option;
        }
        if (Array.isArray(option)) {
            return (_a = option === null || option === void 0 ? void 0 : option.at(0)) === null || _a === void 0 ? void 0 : _a.value;
        }
        return option === null || option === void 0 ? void 0 : option.value;
    }, isOptionEqualToValue = (option, value) => {
        var _a;
        if (!option || !value) {
            return false;
        }
        if (typeof value === 'string') {
            return (option === null || option === void 0 ? void 0 : option.value) === value;
        }
        if (Array.isArray(value)) {
            return (option === null || option === void 0 ? void 0 : option.value) === ((_a = value === null || value === void 0 ? void 0 : value.at(0)) === null || _a === void 0 ? void 0 : _a.value);
        }
        return (option === null || option === void 0 ? void 0 : option.value) === (value === null || value === void 0 ? void 0 : value.value);
    } } = _a, props = __rest(_a, ["defaultPage", "defaultLimit", "lazyQuery", "multiple", "allowSelectAll", "useCheckboxOption", "value", "defaultValue", "onChange", "shouldGroup", "loading", "renderInput", "inputProps", "error", "parseData", "getOptionLabel", "getOptionKey", "isOptionEqualToValue"]);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState();
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(true);
    useEffect(() => {
        setPage(defaultPage);
    }, [defaultPage]);
    useEffect(() => {
        setLoading(loadingProp);
    }, [loadingProp]);
    useEffect(() => {
        if (props.options && props.options.length > 0) {
            setOptions((prevOptions) => Array.from(new Map([...(props.options || []), ...prevOptions].map((option) => [
                option.value,
                option,
            ])).values()));
        }
    }, [props.options]);
    const [fetchItems, { data, loading: queryLoading }] = lazyQuery();
    const icon = _jsx(CheckBoxOutlineBlankIcon, { fontSize: "small" });
    const checkedIcon = _jsx(CheckBoxIcon, { fontSize: "small" });
    const limit = defaultLimit;
    const isControlled = typeof valueProp !== 'undefined';
    const handleOpen = useCallback(() => {
        var _a;
        setOpen(true);
        if (options.length === 0 || options.length === ((_a = props.options) === null || _a === void 0 ? void 0 : _a.length)) {
            fetchItems({
                variables: {
                    pagination: { limit: limit, offset: defaultPage * limit },
                },
            });
        }
    }, [setOpen, options, props.options, fetchItems, limit, defaultPage]);
    const handleClose = () => {
        setOpen(false);
    };
    const debouncedFetch = useCallback(debounce((term) => {
        setLoading(true);
        setPage(defaultPage);
        fetchItems({
            variables: {
                filter: { search: term },
                pagination: { limit: limit, offset: defaultPage * limit },
            },
        });
    }, 500), [setLoading, setPage, defaultPage, fetchItems, limit]);
    const renderInputCb = useMemo(() => {
        if (!renderInput) {
            return (params) => {
                return _jsx(Input, Object.assign({}, params, { error: error }, inputProps));
            };
        }
        return renderInput;
    }, [inputProps, renderInput, error]);
    const handleScroll = useCallback((event) => {
        const listboxNode = event.currentTarget;
        if (hasNextPage &&
            !loading &&
            listboxNode.scrollTop + listboxNode.clientHeight >=
                listboxNode.scrollHeight - limit) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchItems({
                variables: {
                    filter: { search: searchValue },
                    pagination: { limit: limit, offset: nextPage * limit },
                },
            });
        }
    }, [hasNextPage, loading, limit, page, searchValue, fetchItems, setPage]);
    useEffect(() => {
        if (searchValue !== undefined) {
            debouncedFetch(searchValue);
        }
        return () => debouncedFetch.cancel();
    }, [searchValue]);
    useEffect(() => {
        if (data) {
            const { results, hasNextPage } = parseData(data, page, limit);
            setOptions((prevOptions) => {
                return Array.from(new Map([
                    ...(page === defaultPage ? props.options || [] : prevOptions),
                    ...results,
                ].map((option) => [option.value, option])).values());
            });
            setHasNextPage(hasNextPage);
            setLoading(false);
        }
    }, [data, page, limit, parseData, props.options, defaultPage]);
    const getValue = (option) => {
        if (typeof option === 'string') {
            return option;
        }
        if (Array.isArray(option)) {
            return option === null || option === void 0 ? void 0 : option.map((opt) => opt.value);
        }
        return option === null || option === void 0 ? void 0 : option.value;
    };
    const onChange = (event, newValue, reason) => {
        const extendedEvent = Object.assign(Object.assign({}, event), { value: newValue, name: inputProps === null || inputProps === void 0 ? void 0 : inputProps.name, target: Object.assign(Object.assign({}, event.target), { value: getValue(newValue), name: inputProps === null || inputProps === void 0 ? void 0 : inputProps.name }) });
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(extendedEvent, newValue, reason);
    };
    return (_jsx(Autocomplete, Object.assign({ multiple: multiple, open: open, onOpen: handleOpen, onClose: handleClose, loading: loading || queryLoading, filterOptions: (x) => x, onInputChange: (_, value) => {
            setSearchValue(value);
        }, onChange: onChange, ListboxProps: { onScroll: handleScroll }, renderInput: renderInputCb, getOptionLabel: getOptionLabel, getOptionKey: getOptionKey, isOptionEqualToValue: isOptionEqualToValue, defaultValue: isControlled ? undefined : defaultValue, value: isControlled ? valueProp : undefined, renderOption: useCheckboxOption
            ? (props, option, { selected }) => (_jsxs("li", Object.assign({}, props, { children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: { marginRight: 8 }, checked: selected }), option === null || option === void 0 ? void 0 : option.name] })))
            : props.renderOption, groupBy: shouldGroup ? (option) => option.group : undefined, renderGroup: shouldGroup
            ? (params) => (_jsxs("li", { children: [_jsx(GroupHeader, { children: params.group }), _jsx(GroupItems, { children: params.children })] }, params.key))
            : undefined, renderTags: (value, getTagProps) => (_jsx(MultiSelectSelectedTags, { value: value, getTagProps: getTagProps, limitTags: props.limitTags })) }, props, { options: options })));
};
export default ServerSideMultiSelect;
